import NextLink from 'next/link';
import Avatar from '@savvy-kit/components/avatar';
import { Badge } from '@savvy-kit/components/badge';
import { Dialog, DialogContent, DialogTitle } from '@savvy-kit/components/dialog';
import { Text } from '@savvy-kit/foundations/text';
import { cn } from '@savvy-kit/lib/utils';
import { Check, LogOut, Settings, X } from 'lucide-react';

import { useTrackEvent } from '@savvy/deprecated-shared/tracking/use-track-event';

import type { ClientPortalAccountMenuState, ClientPortalNavId, ClientPortalNavState } from './types';
import {
  CLIENT_PORTAL_ACCOUNT_ROUTE,
  CLIENT_PORTAL_MOBILE_MORE_CLOSE_LABEL,
  CLIENT_PORTAL_MOBILE_MORE_SHEET_TITLE,
  CLIENT_PORTAL_MOBILE_OVERFLOW_NAV_ITEMS,
  CLIENT_PORTAL_NAV_ELEMENT_NAMES,
  CLIENT_PORTAL_SETTINGS_LABEL,
  CLIENT_PORTAL_SIGN_OUT_LABEL,
  CLIENT_PORTAL_SWITCH_HOUSEHOLD_LABEL,
  CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES,
} from './constants';
import { clientPortalNavAvailability, clientPortalNavHref, formatTaskBadgeCount, isPresentString } from './utils';

type ClientPortalMobileMoreSheetProps = {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  readonly activeNavId: ClientPortalNavId | null;
  readonly clientName: string;
  readonly clientEmail: string | null;
  readonly clientAvatarUrl: string | null;
  readonly nav: ClientPortalNavState;
  readonly openTaskCount: number;
  readonly accountMenu: ClientPortalAccountMenuState;
};

const SHEET_ROW_CLASS =
  'flex min-h-11 w-full cursor-pointer items-center gap-3 rounded-md px-3 py-2.5 text-left text-[15px] leading-normal font-normal text-dark hover:bg-primary/5';

export function ClientPortalMobileMoreSheet({
  open,
  onOpenChange,
  activeNavId,
  clientName,
  clientEmail,
  clientAvatarUrl,
  nav,
  openTaskCount,
  accountMenu,
}: ClientPortalMobileMoreSheetProps) {
  const trackInteraction = useTrackEvent('userInteraction');

  function close() {
    onOpenChange(false);
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        overlayClassName="ease-in-out data-open:duration-500 data-closed:duration-300"
        className="top-auto bottom-0 left-0 flex max-h-[88vh] w-full max-w-none translate-x-0 translate-y-0 flex-col gap-0 overflow-y-auto rounded-t-xl rounded-b-none border-border-light p-0 ease-in-out sm:max-w-none data-open:fade-in-100 data-open:zoom-in-100 data-open:duration-500 data-open:slide-in-from-bottom data-closed:fade-out-100 data-closed:zoom-out-100 data-closed:duration-300 data-closed:slide-out-to-bottom"
      >
        <div className="sticky top-0 z-10 flex h-12 shrink-0 items-center justify-between border-b border-border-light bg-card px-3">
          <DialogTitle className="text-[15px] leading-normal" render={<span />}>
            {CLIENT_PORTAL_MOBILE_MORE_SHEET_TITLE}
          </DialogTitle>
          <button
            type="button"
            aria-label={CLIENT_PORTAL_MOBILE_MORE_CLOSE_LABEL}
            className="flex size-9 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-light text-dark hover:bg-primary/5"
            onClick={close}
          >
            <X size={20} />
          </button>
        </div>

        <nav aria-label={CLIENT_PORTAL_MOBILE_MORE_SHEET_TITLE} className="p-2">
          {CLIENT_PORTAL_MOBILE_OVERFLOW_NAV_ITEMS.map((item) => {
            const Icon = item.icon;
            const isActive = activeNavId === item.id;
            const href =
              clientPortalNavAvailability(item.id, nav) === 'available'
                ? clientPortalNavHref(item.id, nav.personalPortfolioId)
                : null;
            const count = item.showBadge === true ? openTaskCount : 0;
            const row = (
              <>
                <Icon size={18} active={isActive} />
                <span className="min-w-0 flex-1 truncate">{item.label}</span>
                {count > 0 && (
                  <Badge variant="primary" type="circle-sm">
                    {formatTaskBadgeCount(count)}
                  </Badge>
                )}
              </>
            );

            if (href === null) {
              return (
                <span key={item.id} aria-disabled className={cn(SHEET_ROW_CLASS, 'cursor-default text-secondary/50')}>
                  {row}
                </span>
              );
            }

            return (
              <NextLink
                key={item.id}
                href={href}
                aria-current={isActive ? 'page' : undefined}
                className={cn(SHEET_ROW_CLASS, isActive && 'bg-primary/10 font-semibold text-primary')}
                onClick={() => {
                  trackInteraction({
                    elementName: CLIENT_PORTAL_NAV_ELEMENT_NAMES[item.id],
                    htmlElement: 'link',
                    interactionType: 'button_click',
                  });
                  close();
                }}
              >
                {row}
              </NextLink>
            );
          })}
        </nav>

        <div className="border-t border-border-light p-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))]">
          <div className="flex items-center gap-3 px-3 py-2">
            <Avatar showFallbackIcon className="size-10" imageUrl={clientAvatarUrl} />
            <div className="min-w-0 flex-1">
              <Text variant="subtitle-sm" className="truncate">
                {clientName}
              </Text>
              {isPresentString(clientEmail) && (
                <Text variant="caption" className="truncate">
                  {clientEmail}
                </Text>
              )}
            </div>
          </div>

          <NextLink
            href={CLIENT_PORTAL_ACCOUNT_ROUTE}
            className={cn(SHEET_ROW_CLASS, activeNavId === 'account' && 'bg-primary/10 font-semibold text-primary')}
            aria-current={activeNavId === 'account' ? 'page' : undefined}
            onClick={() => {
              trackInteraction({
                elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.settings,
                htmlElement: 'link',
                interactionType: 'button_click',
              });
              close();
            }}
          >
            <Settings size={18} />
            <span className="min-w-0 flex-1 truncate">{CLIENT_PORTAL_SETTINGS_LABEL}</span>
          </NextLink>

          <button
            type="button"
            className={SHEET_ROW_CLASS}
            onClick={() => {
              trackInteraction({
                elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.logout,
                htmlElement: 'button',
                interactionType: 'button_click',
              });
              close();
              accountMenu.signOut();
            }}
          >
            <LogOut size={18} />
            <span className="min-w-0 flex-1 truncate">{CLIENT_PORTAL_SIGN_OUT_LABEL}</span>
          </button>

          {accountMenu.canSwitchHousehold && (
            <>
              <Text variant="caption" className="block px-3 pt-3 pb-1 font-semibold">
                {CLIENT_PORTAL_SWITCH_HOUSEHOLD_LABEL}
              </Text>
              {accountMenu.households.map((household) => {
                const isActiveHousehold = household.id === accountMenu.activeHouseholdId;
                return (
                  <button
                    key={household.id}
                    type="button"
                    disabled={accountMenu.switching}
                    aria-current={isActiveHousehold ? true : undefined}
                    className={cn(SHEET_ROW_CLASS, 'disabled:cursor-default disabled:opacity-60')}
                    onClick={() => {
                      if (isActiveHousehold) {
                        close();
                        return;
                      }
                      trackInteraction({
                        elementName: CLIENT_PORTAL_USER_MENU_ELEMENT_NAMES.switchHousehold,
                        htmlElement: 'button',
                        interactionType: 'button_click',
                      });
                      close();
                      accountMenu.selectHousehold(household.id);
                    }}
                  >
                    <span className="min-w-0 flex-1 truncate">{household.name}</span>
                    {isActiveHousehold && <Check size={16} className="shrink-0 text-savvy-green" />}
                  </button>
                );
              })}
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
